import { FormattedMessage } from 'react-intl';
import Sidebar from '@rio-cloud/rio-uikit/Sidebar';
import Spinner from '@rio-cloud/rio-uikit/Spinner';

import { SidebarTitleAsset } from '@/components/Asset';
import InternalServerErrorState from '@/components/InternalServerErrorState';
import CustomComponentDialog from '@/features/diagnostics/customComponent/CustomComponentDialog';
import DeleteCustomComponentDialog from '@/features/diagnostics/customComponent/DeleteCustomComponentDialog';
import useCustomComponent from '@/features/diagnostics/hooks/useCustomComponent';
import useCustomComponentForDeletion from '@/features/diagnostics/hooks/useCustomComponentForDeletion';
import { useDiagnosticsDetail } from '@/features/diagnostics/hooks/useDiagnosticsDetail';
import { useVehiclePlan } from '@/features/vehiclePlanDialog/useVehiclePlan';
import VehiclePlanDialog from '@/features/vehiclePlanDialog/VehiclePlanDialog';
import { Asset } from '@/generated/fleetAggregationServiceTypes';
import { hasFeature } from '@/productsConfig';
import { ENGINE_OIL_SENSOR_SIGNAL, isEngineOilSensorSignalV2 } from '@/utils/engineOilUtils';
import { DiagnosticsNotBookedState } from './DiagnosticsNotBooked';
import { DiagnosticsSidebarAssetInformation } from './DiagnosticsSidebarAssetInformation';
import DiagnosticsSidebarTabs from './DiagnosticsSidebarTabs';
import SidebarFooter from './SidebarFooter';

type DiagnosticsSidebarProps = {
    reloadTrigger: object;
    tableReloadTriggerFunction: (assetId: string[]) => void;
    sidebarReloadTriggerFunction: (assetId: string[]) => void;
    assetId: string;
    onClose: (asset?: Asset) => () => void;
};

const DiagnosticsSidebar = ({ reloadTrigger, tableReloadTriggerFunction, sidebarReloadTriggerFunction, onClose, assetId }: DiagnosticsSidebarProps) => {
    const { diagnosticsDetail, isLoading, error } = useDiagnosticsDetail(assetId, reloadTrigger);
    const { showCustomComponentDialog, closeDialog, openCustomComponentDialog, saveCustomComponent, initialCustomComponentState } = useCustomComponent([
        tableReloadTriggerFunction,
        sidebarReloadTriggerFunction,
    ]);

    const { deleteCustomComponent, showDeleteDialog, openDeleteDialog, closeDeleteDialog } = useCustomComponentForDeletion([
        tableReloadTriggerFunction,
        sidebarReloadTriggerFunction,
    ]);

    const mileage = diagnosticsDetail?.latest_events?.measured_at_distance;
    const { vehiclePlanDialogAsset, openVehiclePlanDialog, closeVehiclePlanDialog } = useVehiclePlan();

    const getEngineOil = () => {
        if (!diagnosticsDetail?.engine_oil) {
            return diagnosticsDetail?.engine_oil;
        }

        const hasEngineOilSensorV2 = diagnosticsDetail.engine_oil.items.some((item) => isEngineOilSensorSignalV2(item.signal));

        // If no sensor signal V2 found, return original engine oil data
        if (!hasEngineOilSensorV2) {
            return diagnosticsDetail.engine_oil;
        }

        return {
            ...diagnosticsDetail.engine_oil,
            items: diagnosticsDetail.engine_oil.items.filter((level) => level.signal !== ENGINE_OIL_SENSOR_SIGNAL),
        };
    };

    return (
        <>
            <Sidebar
                fly
                width={600}
                title={
                    <>
                        <FormattedMessage id="intl-msg:fleetstatus.diagnostics.sidebar.title" />
                        <SidebarTitleAsset asset={diagnosticsDetail?.asset} />
                    </>
                }
                showHeaderBorder
                titleClassName="padding-left-10"
                onClose={onClose(diagnosticsDetail?.asset)}
                footer={
                    <SidebarFooter
                        assetId={assetId}
                        openCustomComponentDialog={openCustomComponentDialog}
                        asset={diagnosticsDetail?.asset}
                        openVehiclePlanDialog={openVehiclePlanDialog}
                    />
                }
            >
                {!error && !isLoading && diagnosticsDetail && (
                    <>
                        <div className="padding-left-20 padding-right-20">
                            <DiagnosticsSidebarAssetInformation diagnosticsDetail={diagnosticsDetail} />

                            <DiagnosticsSidebarTabs
                                components={diagnosticsDetail.components}
                                brake_linings={diagnosticsDetail.brake_linings}
                                engine_oil={getEngineOil()}
                                fill_levels={diagnosticsDetail.fill_levels}
                                tire_pressures={diagnosticsDetail.tire_pressures}
                                appointments={diagnosticsDetail.appointments}
                                openCustomComponentDialog={openCustomComponentDialog}
                                openDeleteCustomComponentDialog={openDeleteDialog}
                                asset={diagnosticsDetail.asset}
                                workshopName={diagnosticsDetail.assigned_workshop_name}
                            />
                            {!hasFeature('diagnosticDetailsView', diagnosticsDetail.asset) && <DiagnosticsNotBookedState />}
                        </div>
                    </>
                )}

                {isLoading && (
                    <div className="height-100pct display-flex align-items-center justify-content-center">
                        <Spinner text={<FormattedMessage id="intl-msg:fleetstatus.global.loading" />} />
                    </div>
                )}
                {error && !isLoading && (
                    <InternalServerErrorState reloadTriggerFunction={sidebarReloadTriggerFunction.bind(null, [assetId])} className="margin-right-10 margin-left-10" />
                )}
            </Sidebar>
            {showCustomComponentDialog && (
                <CustomComponentDialog
                    show={showCustomComponentDialog}
                    closeDialog={closeDialog}
                    handleSave={saveCustomComponent}
                    defaultValue={initialCustomComponentState}
                    asset={diagnosticsDetail?.asset || ({} as Asset)}
                    mileage={mileage ? mileage : 0}
                />
            )}

            {showDeleteDialog && (
                <DeleteCustomComponentDialog
                    asset={diagnosticsDetail?.asset || ({} as Asset)}
                    deleteComponent={deleteCustomComponent}
                    closeDialog={closeDeleteDialog}
                    showDialog={showDeleteDialog}
                />
            )}

            <VehiclePlanDialog asset={vehiclePlanDialogAsset} closeDialog={closeVehiclePlanDialog} />
        </>
    );
};

export default DiagnosticsSidebar;
